/**
 * Raincode Sintuation Room — v3.0
 * CRM Workflow Engine · My Tasks · Extended Project Hygiene · Department Routing
 */
import { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "./providers/AuthProvider";
import { cn } from "../lib/cn";
import {
  NotificationBell as ShellNotificationBell,
  ProfileModal as ShellProfileModal,
} from "./components/ShellControls";
import {
  ADMIN_NAV_ITEMS as APP_ADMIN_NAV_ITEMS,
  NAV_ALIAS_MAP,
  NAV_ITEMS as APP_NAV_ITEMS,
} from "./navigation/navConfig";
import TeamPage from "../features/admin/team/pages/TeamPage";
import PermissionsPage from "../features/admin/permissions/pages/PermissionsPage";
import MondayBoard from "../components/MondayBoard";
import AuditsPage from "../features/command-center/audits/pages/AuditsPage";
import DashboardPage from "../features/command-center/dashboard/pages/DashboardPage";
import { ClientDetailPage, ClientListPage } from "../features/command-center/clients/pages/ClientsPage";
import { ProjectDetailPage, ProjectsPage } from "../features/command-center/delivery/pages/DeliveryPages";
import PipelinePage from "../features/command-center/pipeline/pages/PipelinePage";
import DealDetailPage from "../features/command-center/pipeline/pages/DealDetailPage";
import FinancialStatusPage from "../features/command-center/financial/pages/FinancialStatusPage";
import LeadsPage from "../features/command-center/leads/pages/LeadsPage";
import ResourcesPage from "../features/command-center/resources/pages/ResourcesPage";
import WeeklyReportsPage from "../features/command-center/reports/pages/WeeklyReportsPage";
import MentionsPage from "../features/command-center/mentions/pages/MentionsPage";
import ScopingPage from "../features/command-center/scoping/pages/ScopingPage";
import MyTasksPage from "../features/command-center/tasks/pages/MyTasksPage";
import WorkflowDrawerExternal from "../features/command-center/tasks/components/WorkflowDrawer";
import {
  flattenAuditTasksForCount,
  flattenScopingTasksForCount,
  getMyTasksCount,
  normalizeProjectTasksForCount,
} from "../features/command-center/tasks/utils/myTasks";
import AuditTaskTemplatesPage from "../features/command-center/audits/pages/AuditTaskTemplatesPage";
import { CreateClientForm, CreateProjectForm } from "../features/command-center/forms/pages/CreateForms";
import {
  Avt,
  Chip,
  CPTag,
  DBadge,
  DealCatChip,
  DealTypeBadge,
  KpiCard,
  MentionInput,
  PBadge,
  RenderMentions,
  Toast,
  TPChip,
  TSChip,
} from "../shared/components/AppUiAtoms";
import apiClient from "../services/api";
import "../styles/app.css";
import "../styles/charts.css";

// ─────────────────────────────────────────────────────────────────────────────
// IMPORTS
// ─────────────────────────────────────────────────────────────────────────────
import {
  parseMentions,
} from '../constants';
import {
  daysUntil, getInit,
  withUrgency,
} from '../utils';
import {
  mapClient, mapProject, mapTask, mapDeal, mapConsultant,
  mapWeeklyReport, mapScoping, mapLead, mapAudit,
} from '../mappers';

let CURRENT_USER = { name:"Marcus Webb", initials:"MW", role:"ADMIN", avatarUrl:"", email:"", id:"" };

// ── Phase 1 sidebar primitives (migrated from .sb*/.sbi*/.sbi-*badge classes)
// Kept local to this file since they're only used by the sidebar. If the
// pattern gets reused in Phase 5, lift into src/shared/ui/.

function NavGroup({ children, gap }) {
  return (
    <div className={cn(
      "px-2.5 my-[10px] mb-[5px] text-[9px] font-bold uppercase tracking-[0.16em] text-[var(--sb-text-dim)]",
      gap && "mt-3.5",
    )}>
      {children}
    </div>
  );
}

function NavItem({ active, icon, onClick, children }) {
  return (
    <div
      onClick={onClick}
      className={cn(
        "flex items-center gap-2.5 px-3 py-2.5 mb-px rounded-lg",
        "cursor-pointer select-none transition-all duration-[0.12s]",
        "text-[13.5px] font-medium border border-transparent",
        active
          ? "bg-[var(--sb-active-bg)] text-[var(--sb-active-text)] border-[rgba(59,127,250,0.24)]"
          : "text-[var(--sb-text)] hover:bg-[var(--sb-hover-bg)] hover:text-[var(--rc-text-strong)]",
      )}
    >
      <span className={cn(
        "w-[17px] shrink-0 text-center text-[13px]",
        active ? "opacity-100" : "opacity-60",
      )}>
        {icon}
      </span>
      {children}
    </div>
  );
}

function NavBadge({ tone = "info", children }) {
  const bg = tone === "alert" ? "bg-rc-red" : tone === "warn" ? "bg-[#d97706]" : "bg-rc-accent";
  return (
    <span className={cn(
      "ml-auto rounded-[10px] px-[5px] py-px text-[9px] font-bold text-white",
      bg,
    )}>
      {children}
    </span>
  );
}

export default function App(){
  const navigateToRoute = useNavigate();
  const location = useLocation();
  const { user, logout, isAdmin, isSuperAdmin, canSeePage, impersonating, activeRole, setImpersonateRole } = useAuth();
  const routePageMap = { "/audits": "audits", "/audit-task-templates": "audit-task-templates", "/mentions": "mentions" };
  const pageRouteMap = { audits: "/audits", "audit-task-templates": "/audit-task-templates", mentions: "/mentions" };
  const routedPage = routePageMap[location.pathname] || null;
  const savedNavState = (() => {
    try {
      const raw = sessionStorage.getItem("rc.nav");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  })();

  // Update CURRENT_USER with authenticated user data
  useEffect(() => {
    if (user) {
      CURRENT_USER = {
        name: user.name,
        initials: user.initials || getInit(user.name || ""),
        role: user.role,
        avatarUrl: user.avatarUrl || "",
        email: user.email || "",
        id: user.id || "",
      };
    }
  }, [user]);

  const [page,    setPage]    =useState(routedPage || savedNavState?.page || "dashboard");
  const [param,   setParam]   =useState(savedNavState?.param || null);
  const [deals,        setDeals]        = useState([]);
  const [projects,     setProjects]     = useState([]);
  const [clients,      setClients]      = useState([]);
  const [comments,     setComments]     = useState({});
  const [tasks,        setTasks]        = useState([]);
  const [consultants,  setConsultants]  = useState([]);
  const [toast,        setToast]        = useState(null);
  const [weeklyReports,setWeeklyReports]= useState([]);
  const [monthlyReport,setMonthlyReport]= useState(null);
  const [scoping,      setScoping]      = useState([]);
  const [leads,        setLeads]        = useState([]);
  const [audits,       setAudits]       = useState([]);
  const [teamsData,    setTeamsData]    = useState([]);
  const [dataLoading,  setDataLoading]  = useState(false);
  const [notifications,setNotifications]=useState([]);
  const [showProfile,  setShowProfile]  = useState(false);
  const [sidebarOpen,  setSidebarOpen]  = useState(false);
  const [profileUser,  setProfileUser]  = useState(user);
  const [theme, setTheme] = useState(() => localStorage.getItem("theme") || "light");

  useEffect(() => {
    localStorage.setItem("theme", theme);
  }, [theme]);

  useEffect(() => {
    try {
      sessionStorage.setItem("rc.nav", JSON.stringify({ page, param }));
    } catch {}
  }, [page, param]);

  useEffect(() => {
    if (!routedPage) return;
    setPage(routedPage);
    setParam(null);
  }, [routedPage]);

  // Fetch all data from API when user is authenticated
  useEffect(() => {
    if (!user) return;
    setDataLoading(true);
    Promise.all([
      apiClient.getClients(),
      apiClient.getProjects(),
      apiClient.getTasks(),
      apiClient.getDeals(),
      apiClient.getConsultants(),
      apiClient.getWeeklyReports(),
      apiClient.getScoping(),
      apiClient.getLeads(),
      apiClient.getAudits(),
      apiClient.getTeams(),
    ]).then(([
      clientsData, projectsData, tasksData, dealsData,
      consultantsData, weeklyReportsData, scopingData, leadsData, auditsData, teamsResponse,
    ]) => {
      setClients(clientsData.map(mapClient));
      setProjects(projectsData.map(mapProject));
      setTasks(tasksData.map(mapTask));
      setDeals(dealsData.map(mapDeal));
      setConsultants(consultantsData.map(mapConsultant));
      setWeeklyReports(weeklyReportsData.map(mapWeeklyReport));
      setScoping(scopingData.map(mapScoping));
      setLeads(leadsData.map(mapLead));
      setAudits(auditsData.map(mapAudit));
      const teamList = Array.isArray(teamsResponse?.teams) ? teamsResponse.teams : [];
      setTeamsData(teamList.map(team=>({
        ...team,
        members: Array.isArray(team.members) ? team.members : [],
        stageLabel: team.stage,
      })));
      // Build comments map from clients
      const commentMap = {};
      clientsData.forEach(c => { commentMap[c.name] = []; });
      setComments(commentMap);
    }).catch(err => {
      console.error('Failed to load data:', err);
    }).finally(() => {
      setDataLoading(false);
    });
  }, [user]);

  function nav(t,p=null){
    setPage(t);
    setParam(p);
    const nextPath = pageRouteMap[t] || "/";
    if (location.pathname !== nextPath) navigateToRoute(nextPath);
    document.querySelector(".rc-main")?.scrollTo(0,0);
  }
  function showToast(title,sub){setToast({title,sub});setTimeout(()=>setToast(null),3200);}

  async function onClientSubmit(d) {
    try {
      const created = await apiClient.createClient({ name: d.clientName || d.name, ...d });
      setClients(cs => [...cs, mapClient(created)]);
      setComments(co => ({ ...co, [created.name]: [] }));
      showToast("Client created", "Account added");
      nav("clients");
    } catch (err) {
      showToast("Error", err?.response?.data?.error || "Failed to create client");
    }
  }

  async function onProjectSubmit(d) {
    try {
      // Inline client-creation path: the Create Project form can register a
      // brand-new Client in the same submission. Create the Client first so
      // we have a real clientId to attach to the Project.
      let clientId = d.clientId;
      if (d.createNewClient && d.newClient) {
        const createdClient = await apiClient.createClient({
          name: d.newClient.name,
          csm: d.newClient.csm,
          contactPerson: d.newClient.contactPerson,
          email: d.newClient.email,
        });
        clientId = createdClient.id || createdClient.clientId;
        setClients(cs => [...cs, mapClient(createdClient)]);
        setComments(co => ({ ...co, [createdClient.name]: [] }));
      }
      const created = await apiClient.createProject({
        name: d.projectName || d.name,
        ...d,
        clientId,
      });
      setProjects(ps => [...ps, mapProject(created)]);
      showToast("Project created", `${created.name} added`);
      nav("projects");
    } catch (err) {
      showToast("Error", err?.response?.data?.error || "Failed to create project");
    }
  }

  // Keep profileUser in sync with auth user
  useEffect(()=>{setProfileUser(user);},[user]);

  // Fetch notifications from API, poll every 60s
  useEffect(()=>{
    if(!user)return;
    let cancelled = false;

    function fetchNotifs(){
      apiClient.getNotifications().then(d=>{setNotifications(d.notifications||[]);}).catch(()=>{});
    }

    function refreshOnVisibility(){
      if (document.visibilityState === "visible" && !cancelled) {
        fetchNotifs();
      }
    }

    fetchNotifs();
    window.addEventListener("focus", fetchNotifs);
    document.addEventListener("visibilitychange", refreshOnVisibility);
    const interval=setInterval(fetchNotifs,15000);
    return()=>{
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener("focus", fetchNotifs);
      document.removeEventListener("visibilitychange", refreshOnVisibility);
    };
  },[user]);

  async function addNotification(n){
    try{
      const payload={
        userId:n.userId,
        type:String(n.type||"SYSTEM").toUpperCase(),
        title:n.title||"",
        text:n.text||"",
        link:n.link||null,
      };
      if(!payload.userId||!payload.text) return null;
      const response=await apiClient.createNotification(payload);
      const saved=response?.notification||response;
      setNotifications(ns=>[saved,...ns.filter(entry=>entry.id!==saved.id)].slice(0,49));
      return saved;
    }catch{
      const fallback={...n,id:n.id||`local-${Date.now()}`,read:false,createdAt:new Date().toISOString()};
      setNotifications(ns=>[fallback,...ns.slice(0,49)]);
      return fallback;
    }
  }
  function markNotifRead(id){
    apiClient.markNotificationRead(id).catch(()=>{});
    setNotifications(ns=>ns.map(n=>n.id===id?{...n,read:true}:n));
  }
  function markAllNotifsRead(){
    apiClient.markAllNotificationsRead().catch(()=>{});
    setNotifications(ns=>ns.map(n=>({...n,read:true})));
  }
  function clearAllNotifs(){
    apiClient.clearAllNotifications().catch(()=>{});
    setNotifications([]);
  }

  async function handleProfileSave(name){
    try{
      const r=await apiClient.updateProfile({name});
      const nextUser=r?.user||r;
      if(nextUser){
        const normalizedUser={
          ...nextUser,
          initials:nextUser.initials||getInit(nextUser.name||""),
        };
        CURRENT_USER={
          name:normalizedUser.name,
          initials:normalizedUser.initials,
          role:normalizedUser.role,
          avatarUrl:normalizedUser.avatarUrl||"",
          email:normalizedUser.email||"",
        };
        setProfileUser(normalizedUser);
        localStorage.setItem("user",JSON.stringify(normalizedUser));
      }
    }catch(e){showToast("Error","Failed to save profile");}
  }

  const tasksU=useMemo(()=>withUrgency(tasks,projects),[tasks,projects]);
  const alertCount=projects.filter(p=>p.weeklyReportStatus==="Overdue"||p.projectStatus==="Critical").length;
  const currentUserId = profileUser?.id || user?.id || CURRENT_USER.id;
  const myActive = useMemo(() => getMyTasksCount({
    tasks: normalizeProjectTasksForCount(tasks),
    scopingTasks: flattenScopingTasksForCount(scoping),
    auditTasks: flattenAuditTasksForCount(audits),
    currentUserId,
  }), [audits, currentUserId, scoping, tasks]);
  const myOD=tasksU.filter(t=>t.assignee===CURRENT_USER.name&&daysUntil(t.dueDate)<0&&t.status!=="Done"&&t.status!=="Cancelled").length;
  const activeNav = NAV_ALIAS_MAP[page] || page;
  // codex.md RBAC — filter both nav groups through the permission grid.
  // SUPER_ADMIN / CEO short-circuit to true inside canSeePage, so they
  // always see everything even before the seed completes. During role
  // impersonation we intentionally reflect the pretend role's grid, but
  // we keep the Permissions admin item reachable (via isSuperAdmin on the
  // real user) so the admin can always stop previewing.
  const visibleNavItems = APP_NAV_ITEMS.filter((item) => canSeePage(item.pageKey || item.id));
  const visibleAdminNavItems = APP_ADMIN_NAV_ITEMS
    .filter((item) => !item.requiresSuperAdmin || isSuperAdmin)
    .filter((item) => {
      // Permissions admin page is always visible to the real SUPER_ADMIN
      // so role-preview is never a one-way door.
      if (item.pageKey === "permissions" && isSuperAdmin) return true;
      return canSeePage(item.pageKey || item.id);
    });
  const unreadMentionCount = notifications.filter((notification) => notification.type === "MENTION" && !notification.read).length;

  function render(){
    if (dataLoading) return (
      <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"100%",flexDirection:"column",gap:16,color:"var(--rc-text-secondary)"}}>
        <div style={{width:32,height:32,border:"3px solid var(--rc-border)",borderTopColor:"rgb(64,117,193)",borderRadius:"50%",animation:"spin 0.8s linear infinite"}}/>
        <div style={{fontSize:13,fontWeight:500}}>Loading…</div>
      </div>
    );
    // codex.md RBAC: guard against direct-URL access to a gated page.
    // During role impersonation the real SUPER_ADMIN can still reach the
    // Permissions admin page so they can stop previewing; every other
    // route respects the impersonated role's grid.
    if (!canSeePage(activeNav) && !(activeNav === "permissions" && isSuperAdmin)) {
      return (
        <div className="fade" style={{padding:"32px 36px"}}>
          <div className="pg-title">Access Denied</div>
          <div className="pg-sub">
            You don&apos;t have permission to view this page.{" "}
            {impersonating
              ? <>You&apos;re previewing as <strong>{activeRole}</strong>. <button onClick={() => setImpersonateRole(null)} style={{marginLeft:6,padding:"2px 8px",borderRadius:4,border:"1px solid var(--rc-border)",background:"#070e20",color:"#8fa4c8",cursor:"pointer",fontSize:12}}>Stop previewing</button></>
              : "Ask a Super Admin if you need access."}
          </div>
        </div>
      );
    }
    switch(page){
      case "dashboard":   return <DashboardPage deals={deals} projects={projects} tasks={tasks} consultants={consultants} nav={nav} KpiCardComponent={KpiCard} AvatarComponent={Avt} PriorityBadgeComponent={PBadge}/>;
      case "mytasks":     return <MyTasksPage tasks={tasks} projects={projects} setTasks={setTasks} nav={nav} addNotification={addNotification} scoping={scoping} setScoping={setScoping} audits={audits} setAudits={setAudits} leads={leads} setLeads={setLeads} currentUser={CURRENT_USER} AvatarComponent={Avt} StatusChipComponent={TSChip} PriorityChipComponent={TPChip} DepartmentBadgeComponent={DBadge} CustomerPriorityTagComponent={CPTag} MentionInputComponent={MentionInput} RenderMentionsComponent={RenderMentions}/>;

      case "pipeline":    return <PipelinePage deals={deals} setDeals={setDeals} nav={nav} KpiCardComponent={KpiCard} DealCatChipComponent={DealCatChip} DealTypeBadgeComponent={DealTypeBadge}/>;
      case "scoping":     return <ScopingPage scoping={scoping} setScoping={setScoping} consultants={consultants} nav={nav} showToast={showToast} addNotification={addNotification} currentUser={CURRENT_USER} AvatarComponent={Avt}/>;
      case "projects":    return <ProjectsPage projects={projects} setProjects={setProjects} consultants={consultants} tasks={tasks} setTasks={setTasks} showToast={showToast} nav={nav} KpiCardComponent={KpiCard} WorkflowDrawerComponent={WorkflowDrawerExternal} StatusChipComponent={TSChip} DepartmentBadgeComponent={DBadge} PriorityBadgeComponent={PBadge} CustomerPriorityTagComponent={CPTag} ChipComponent={Chip} AvatarComponent={Avt}/>;
      case "resources":   return <ResourcesPage consultants={consultants} setConsultants={setConsultants} deals={deals} nav={nav} showToast={showToast} KpiCardComponent={KpiCard} AvatarComponent={Avt}/>;
      case "clients":     return <ClientListPage projects={projects} clients={clients} comments={comments} nav={nav} showToast={showToast} KpiCardComponent={KpiCard} PriorityBadgeComponent={PBadge}/>;
      case "client":      return <ClientDetailPage clientId={param} projects={projects} comments={comments} setComments={setComments} addNotification={addNotification} nav={nav} showToast={showToast} currentUser={CURRENT_USER} AvatarComponent={Avt} ChipComponent={Chip} PriorityBadgeComponent={PBadge} CommentAtoms={{ MentionInput, RenderMentions, parseMentions }}/>;
      case "deal":        return <DealDetailPage deal={deals.find(d=>d.dealId===param)} nav={nav} KpiCardComponent={KpiCard}/>;
      case "project":     return <ProjectDetailPage project={projects.find(p=>p.projectId===param)} tasks={tasks} setTasks={setTasks} setProjects={setProjects} consultants={consultants} showToast={showToast} nav={nav} KpiCardComponent={KpiCard} ChipComponent={Chip} PriorityBadgeComponent={PBadge} CustomerPriorityTagComponent={CPTag} DepartmentBadgeComponent={DBadge} StatusChipComponent={TSChip} PriorityChipComponent={TPChip} AvatarComponent={Avt}/>;
      case "financial":   return <FinancialStatusPage projects={projects} scoping={scoping} leads={leads} consultants={consultants} monthlyReport={monthlyReport} setMonthlyReport={setMonthlyReport} showToast={showToast} addNotification={addNotification} currentUser={CURRENT_USER} KpiCardComponent={KpiCard} ChipComponent={Chip}/>;
      case "reports":     return <WeeklyReportsPage weeklyReports={weeklyReports} setWeeklyReports={setWeeklyReports} projects={projects} tasks={tasks} addNotification={addNotification} showToast={showToast} currentUser={CURRENT_USER}/>;
      case "mentions":    return <MentionsPage />;
      case "leads":       return <LeadsPage leads={leads} setLeads={setLeads} scoping={scoping} setScoping={setScoping} nav={nav} showToast={showToast} currentUser={CURRENT_USER} AvatarComponent={Avt}/>;
      case "audits":      return <AuditsPage  audits={audits} setAudits={setAudits} showToast={showToast} currentUser={CURRENT_USER} addNotification={addNotification} teams={teamsData}/>;
      case "audit-task-templates": return <AuditTaskTemplatesPage showToast={showToast}/>;
      case "permissions": return isSuperAdmin
        ? <PermissionsPage showToast={showToast}/>
        : (
          <div className="fade" style={{padding:"32px 36px"}}>
            <div className="pg-title">Access Denied</div>
            <div className="pg-sub">Permissions admin is restricted to Super Admins.</div>
          </div>
        );
      case "monday":      return <MondayBoard />;
      case "team":        return isSuperAdmin
        ? <TeamPage currentUser={profileUser||user} teams={teamsData} setTeams={setTeamsData}/>
        : (
          <div className="fade" style={{padding:"32px 36px"}}>
            <div className="pg-title">Access Denied</div>
            <div className="pg-sub">Team &amp; Access is restricted to Super Admins.</div>
          </div>
        );
      case "new-client":  return <CreateClientForm onSubmit={onClientSubmit} onCancel={()=>nav("clients")}/>;
      case "new-project": return <CreateProjectForm clients={clients} onSubmit={onProjectSubmit} onCancel={()=>nav("projects")} currentUser={CURRENT_USER}/>;
      default: return null;
    }
  }

  const storedUser = (() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  })();
  const currentUserForDisplay = profileUser || user || storedUser || {name:CURRENT_USER.name,initials:CURRENT_USER.initials,role:CURRENT_USER.role,avatarUrl:CURRENT_USER.avatarUrl,email:CURRENT_USER.email};
  const roleDisplayLabel = {SUPER_ADMIN:"Super Admin",ADMIN:"Admin",MANAGER:"Manager",CONSULTANT:"Consultant",CLIENT_VIEW:"Client View"}[currentUserForDisplay.role]||currentUserForDisplay.role||"";

  // Phase 0 of the Tailwind migration: `.dark` is always applied so
  // Tailwind's `dark:` modifier always resolves. The existing data-theme
  // attribute is left in place so every legacy CSS rule keyed off
  // [data-theme="light"] keeps working until Phase 5 removes it.
  return (
    <div className={`rc dark`} data-theme={theme}>
      {/* Mobile sidebar overlay — visible when sidebarOpen on screens <768px */}
      <div
        onClick={()=>setSidebarOpen(false)}
        className={cn(
          "fixed inset-0 z-[399] bg-[rgba(4,9,26,0.7)]",
          sidebarOpen ? "block" : "hidden",
        )}
      />

      {/*
        Sidebar. Desktop: 236px flex child. Mobile (max-md / <768px):
        fixed slide-in. CSS vars --sb-* still win here so the existing
        [data-theme="light"] toggle keeps recolouring it until Phase 5
        removes the attribute entirely.
      */}
      <aside
        className={cn(
          "flex flex-col shrink-0 w-[236px] min-w-[236px] overflow-y-auto",
          "bg-[var(--sb-bg)] border-r border-[var(--sb-border)]",
          "max-md:fixed max-md:inset-y-0 max-md:left-0 max-md:z-[400] max-md:shadow-rc-lg",
          "max-md:transition-transform max-md:duration-[250ms]",
          sidebarOpen ? "max-md:translate-x-0" : "max-md:-translate-x-full",
        )}
      >
        <div className="shrink-0 px-5 pt-[22px] pb-[18px] border-b border-[var(--sb-border)]">
          <img rel="icon" src="/logo/logo-raincode-white.svg" alt="Raincode" style={{ width: 100, marginBottom: 0 }}/>
          <div className="mt-1 text-[19px] leading-[1.2] font-extrabold text-[var(--rc-text-primary)]">Situation<br/>Room</div>
          <div className="mt-[3px] text-[9.5px] uppercase tracking-[0.1em] text-[var(--sb-text-dim)]">Enterprise Platform</div>
        </div>
        <nav className="flex-1 py-3.5 px-3">
          <NavGroup>Main</NavGroup>
          {visibleNavItems.map(item=>(
            <NavItem key={item.id} active={activeNav===item.id} icon={item.icon} onClick={()=>{nav(item.id);setSidebarOpen(false);}}>
              <span>{item.label}</span>
              {item.id==="projects"&&alertCount>0&&<NavBadge tone="alert">{alertCount}</NavBadge>}
              {item.id==="mytasks"&&myActive>0&&<NavBadge tone={myOD>0?"alert":"info"}>{myActive}</NavBadge>}
              {item.id==="mentions"&&unreadMentionCount>0&&<NavBadge tone="info">{unreadMentionCount}</NavBadge>}
              {item.id==="resources"&&consultants.filter(c=>c.status==="Bench").length>0&&<NavBadge tone="info">{consultants.filter(c=>c.status==="Bench").length}</NavBadge>}
              {item.id==="financial"&&monthlyReport&&monthlyReport.status!=="generated"&&<NavBadge tone="warn">!</NavBadge>}
            </NavItem>
          ))}
          <NavGroup gap>Create</NavGroup>
          <NavItem active={page==="new-client"} icon="+" onClick={()=>{nav("new-client");setSidebarOpen(false);}}><span>New Client</span></NavItem>
          <NavItem active={page==="new-project"} icon="+" onClick={()=>{nav("new-project");setSidebarOpen(false);}}><span>New Project</span></NavItem>
          {isAdmin&&<>
            <NavGroup gap>Admin</NavGroup>
            {visibleAdminNavItems.map(item=>(
              <NavItem key={item.id} active={activeNav===item.id} icon={item.icon} onClick={()=>{nav(item.id);setSidebarOpen(false);}}>
                <span>{item.label}</span>
              </NavItem>
            ))}
          </>}
        </nav>
        <div className="shrink-0 flex flex-col gap-2 px-5 py-3.5 border-t border-[var(--sb-border)]">
          <div onClick={()=>setShowProfile(true)} title="View profile" className="flex items-center gap-2.5 cursor-pointer text-[var(--sb-text)]">
            {currentUserForDisplay.avatarUrl
              ? <img src={currentUserForDisplay.avatarUrl} alt={currentUserForDisplay.name} className="w-8 h-8 rounded-full object-cover shrink-0"/>
              : <Avt initials={currentUserForDisplay.initials||getInit(currentUserForDisplay.name||"")} size={32}/>
            }
            <div className="min-w-0 flex-1">
              <div className="overflow-hidden whitespace-nowrap text-ellipsis text-[12.5px] font-semibold text-[var(--sb-text)]">{currentUserForDisplay.name}</div>
              <div className="mt-px text-[10.5px] text-[var(--sb-text-dim)]">{roleDisplayLabel}</div>
            </div>
            <svg width="13" height="13" viewBox="0 0 20 20" fill="none" className="shrink-0 opacity-50">
              <circle cx="10" cy="10" r="3" stroke="currentColor" strokeWidth="1.8"/>
              <path d="M10 2v2M10 16v2M2 10h2M16 10h2M4.22 4.22l1.42 1.42M14.36 14.36l1.42 1.42M4.22 15.78l1.42-1.42M14.36 5.64l1.42-1.42" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
          </div>
          <div className="flex items-center justify-between gap-2">
            <button
              onClick={()=>setTheme(t=>t==="dark"?"light":"dark")}
              className={cn(
                "px-2.5 py-[5px] rounded-full bg-transparent cursor-pointer font-sans font-bold text-[9.5px]",
                "border border-[var(--sb-border)] text-[var(--sb-text)]",
                "transition-colors duration-[0.12s]",
                "hover:bg-[var(--sb-hover-bg)] hover:text-[var(--rc-text-strong)]",
              )}
            >
              {theme==="dark" ? "Light" : "Dark"}
            </button>
            <div className="mt-2.5 text-[10px] font-mono text-rc-blue-450">v5.0.0-MVP</div>
          </div>
        </div>
      </aside>

      <main className="rc-main">
        {/* Role-impersonation banner — only when a SUPER_ADMIN has picked a
            preview role. Real role is preserved server-side; this only
            affects the local canSeePage / canSeeGraph decisions so the UI
            reflects what the picked role would see. */}
        {impersonating && (
          <div style={{
            padding: "8px 14px",
            background: "rgba(245,158,11,.14)",
            borderBottom: "1px solid rgba(245,158,11,.4)",
            color: "#f59e0b",
            fontSize: 12,
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}>
            <span>⚠ Previewing as {activeRole}. Sidebar + pages reflect that role.</span>
            <button
              onClick={() => setImpersonateRole(null)}
              style={{
                marginLeft: "auto",
                padding: "3px 10px",
                borderRadius: 6,
                border: "1px solid rgba(245,158,11,.5)",
                background: "transparent",
                color: "#f59e0b",
                cursor: "pointer",
                fontSize: 11,
                fontWeight: 700,
              }}
            >
              ✕ Stop previewing
            </button>
          </div>
        )}
        <div className="shell-top-actions">
          <ShellNotificationBell notifications={notifications} onMarkRead={markNotifRead} onMarkAllRead={markAllNotifsRead} onClearAll={clearAllNotifs} onOpenMentions={()=>nav("mentions")}/>
        </div>
        {/* Mobile top bar */}
        <div className="mob-topbar">
          <button className="mob-menu-btn" onClick={()=>setSidebarOpen(o=>!o)}>☰</button>
        </div>
        {render()}
      </main>

      <Toast toast={toast}/>

      {showProfile&&(
        <ShellProfileModal
          user={currentUserForDisplay}
          onClose={()=>setShowProfile(false)}
          onSave={handleProfileSave}
          onLogout={async()=>{setShowProfile(false);await logout();}}
        />
      )}
    </div>
  );
}






